import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loading, Card, PageHeader, EmptyState } from "@/components/common";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3 } from "lucide-react";

const COLORS = ["#123B63", "#3F7FB5", "#087F83", "#D6A01D", "#64748B", "#164A78", "#1D5A90"];

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loading />;
  if (!d) return <EmptyState icon={BarChart3} title="Unable to load analytics" />;

  const statusData = Object.entries(d.status_distribution).map(([name, value]) => ({ name, value }));
  const policyData = Object.entries(d.policy_distribution).map(([name, value]) => ({ name, value }));
  const tooltipStyle = { background: "#FFFFFF", border: "1px solid #DCE3EA", borderRadius: 8, color: "#172033", boxShadow: "0 4px 16px rgba(16,33,53,0.08)" };

  return (
    <div>
      <PageHeader title="Executive Analytics" subtitle="Current-session trends and legislative activity." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="chart-status">
          <h3 className="font-display font-semibold text-brand-ink mb-4">Bill counts by status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7ECF2" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fill: "#64748B", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(63,127,181,0.08)" }} />
              <Bar dataKey="value" fill="#3F7FB5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card data-testid="chart-policy">
          <h3 className="font-display font-semibold text-brand-ink mb-4">Activity by policy area</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={policyData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={{ fill: "#475569", fontSize: 11 }}>
                {policyData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
      <Card className="mt-4" data-testid="analytics-summary">
        <h3 className="font-display font-semibold text-brand-ink mb-3">Session at a glance</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(d.summary).map(([k, v]) => (
            <div key={k} className="rounded-lg bg-brand-bg border border-brand-line px-4 py-3"><div className="font-display text-2xl font-extrabold text-brand-navy">{v}</div><div className="text-xs text-slate-500 capitalize mt-0.5">{k.replace(/_/g, " ")}</div></div>
          ))}
        </div>
      </Card>
    </div>
  );
}
